import { inject, Service } from '@angular/core';
import { FeatureUserStore } from '@h5s/feature-user';
import { AuthService } from '@h5s/util-auth';
import { AppError } from '@h5s/util-error';
import { ExternalLoggerService, LogInfo } from '@h5s/util-logger';
import { tryParseJson } from '@h5s/util-string';
import Rollbar from 'rollbar';

@Service()
export class FeatureLogService {
  private readonly authService = inject(AuthService);
  private readonly featureUserStore = inject(FeatureUserStore);
  private readonly externalLoggerService = inject(ExternalLoggerService);
  private readonly externalLogger = this.externalLoggerService.getLogger();

  public buildPayloadBaseInfo(payload: Rollbar.LogArgument[]): void {
    payload.push(
      { userSub: this.authService.getUserSub() },
      { customerCode: this.featureUserStore.userAccessProperty().customerCode },
      { propertyName: this.featureUserStore.userAccessProperty().propertyName },
      { groupName: this.featureUserStore.userAccessProperty().groupName ?? 'N/D' },
    );
  }

  public buildPayloadForExternalLogger(model: LogInfo | AppError): Rollbar.LogArgument[] {
    const payload: Rollbar.LogArgument[] = [];

    const causeIsAppError = model instanceof AppError && model.cause instanceof AppError;

    // Se la causa è a sua volta un AppError, raccoglie prima le sue info così che
    // le info dell'errore esterno (aggiunte dopo) abbiano la precedenza in caso di chiavi duplicate.
    if (causeIsAppError) {
      (model.cause as AppError).info.forEach((value, key) => payload.push({ [key]: tryParseJson(value) }));
    }

    // Quando la causa è un AppError, `original-error-message` e `original-error-name`
    // ripetono solo il messaggio/nome della causa, già rappresentati in modo più preciso
    // dai campi specifici (requestUrl, http-error-code, ecc.). Li omettiamo.
    const REDUNDANT_WITH_CAUSE = new Set(['original-error-message', 'original-error-name']);
    model.info.forEach((value, key) => {
      if (causeIsAppError && REDUNDANT_WITH_CAUSE.has(key)) return;
      payload.push({ [key]: tryParseJson(value) });
    });

    this.buildPayloadBaseInfo(payload);
    return payload;
  }

  public logExternal(model: LogInfo | AppError): void {
    const payload = this.buildPayloadForExternalLogger(model);
    this.externalLogger.log(true, model.message, payload);
  }

  public warnExternal(model: LogInfo | AppError): void {
    const payload = this.buildPayloadForExternalLogger(model);
    this.externalLogger.warn(model.message, payload);
  }

  public errorExternal(model: LogInfo | AppError): void {
    const payload = this.buildPayloadForExternalLogger(model);
    this.externalLogger.error(model.message, payload);
  }

  public infoExternal(model: LogInfo | AppError): void {
    const payload = this.buildPayloadForExternalLogger(model);
    this.externalLogger.info(model.message, payload);
  }

  public debugExternal(model: LogInfo | AppError): void {
    const payload = this.buildPayloadForExternalLogger(model);
    this.externalLogger.debug(model.message, payload);
  }
}
